const MODALITY_LABELS = {
    CR: 'X-Ray',
    DX: 'X-Ray',
    RG: 'X-Ray',
    PX: 'X-Ray',
    IO: 'X-Ray',
    XA: 'Angiography',
    RF: 'Fluoroscopy',
    CT: 'CT',
    MR: 'MRI',
    US: 'Ultrasound',
    NM: 'Nuclear Medicine',
    PT: 'PET',
    MG: 'Mammography',
    DXA: 'DEXA',
    BMD: 'DEXA',
    ES: 'Endoscopy',
    OP: 'Ophthalmic',
    ECG: 'ECG',
};

const NON_IMAGE_MODALITIES = new Set(['SR', 'KO', 'PR', 'SEG', 'REG', 'RTSTRUCT', 'RTPLAN', 'RTDOSE', 'DOC', 'OT']);

const LATERAL_REGIONS = [
    { name: 'Hip', pattern: /\bhips?\b/, bodyParts: ['HIP'] },
    { name: 'Knee', pattern: /\bknees?\b/, bodyParts: ['KNEE'] },
    { name: 'Shoulder', pattern: /\bshoulders?\b/, bodyParts: ['SHOULDER'] },
    { name: 'Elbow', pattern: /\belbows?\b/, bodyParts: ['ELBOW'] },
    { name: 'Wrist', pattern: /\bwrists?\b/, bodyParts: ['WRIST'] },
    { name: 'Hand', pattern: /\bhands?\b/, bodyParts: ['HAND'] },
    { name: 'Ankle', pattern: /\bankles?\b/, bodyParts: ['ANKLE'] },
    { name: 'Foot', pattern: /\bfoot\b|\bfeet\b/, bodyParts: ['FOOT'] },
    { name: 'Femur', pattern: /\bfemurs?\b|\bfemoral\b/, bodyParts: ['FEMUR'] },
    { name: 'Tibia', pattern: /\btibias?\b|\btib[\s/-]?fib\b/, bodyParts: ['TIBIA', 'TIBIAFIBULA'] },
    { name: 'Humerus', pattern: /\bhumerus\b/, bodyParts: ['HUMERUS'] },
    { name: 'Forearm', pattern: /\bforearms?\b/, bodyParts: ['FOREARM', 'RADIUSULNA'] },
    { name: 'Lower Extremity', pattern: /\blower\s+ext(remit(y|ies))?\b|\bscanogram\b|\bleg\s+length\b|\blong\s+legs?\b/, bodyParts: ['LEG', 'LOWERLEG', 'EXTREMITY', 'LOWEREXTREMITY'] },
    { name: 'Upper Extremity', pattern: /\bupper\s+ext(remit(y|ies))?\b/, bodyParts: ['ARM', 'UPPEREXTREMITY'] },
];

const CENTRAL_REGIONS = [
    { name: 'Lumbar Spine', pattern: /\blumbar\b|\bl[\s_-]?spine\b|\blspine\b|\bl[1-5][\s-]*(?:[-–]|to)?[\s-]*(?:l[1-5]|s1)\b|\bl-?s\s+spine\b/, bodyParts: ['LSPINE', 'LUMBARSPINE', 'LSSPINE'] },
    { name: 'Cervical Spine', pattern: /\bcervical\b|\bc[\s_-]?spine\b|\bcspine\b/, bodyParts: ['CSPINE', 'CERVICALSPINE'] },
    { name: 'Thoracic Spine', pattern: /\bthoracic\s+spine\b|\bt[\s_-]?spine\b|\btspine\b|\bdorsal\s+spine\b/, bodyParts: ['TSPINE', 'THORACICSPINE'] },
    { name: 'Sacrum', pattern: /\bsacr(um|al)\b|\bsi\s+joints?\b|\bsacroiliac\b|\bcoccyx\b/, bodyParts: ['SACRUM', 'SIJOINT', 'COCCYX', 'SSPINE'] },
    { name: 'Spine', pattern: /\bspine\b|\bspinal\b|\bscoliosis\b/, bodyParts: ['SPINE', 'WHOLESPINE'] },
    { name: 'Pelvis', pattern: /\bpelvis\b|\bpelvic\b/, bodyParts: ['PELVIS', 'ABDOMENPELVIS'] },
    { name: 'Abdomen', pattern: /\babdom(en|inal)\b/, bodyParts: ['ABDOMEN', 'ABDOMENPELVIS'] },
    { name: 'Chest', pattern: /\bchest\b|\bthorax\b|\blungs?\b/, bodyParts: ['CHEST', 'THORAX', 'LUNG'] },
    { name: 'Brain', pattern: /\bbrain\b|\bhead\b|\bskull\b/, bodyParts: ['BRAIN', 'HEAD', 'SKULL'] },
    { name: 'Neck', pattern: /\bneck\b/, bodyParts: ['NECK'] },
    { name: 'Heart', pattern: /\bcardiac\b|\bheart\b/, bodyParts: ['HEART'] },
    { name: 'Whole Body', pattern: /\bwhole\s+body\b|\bfull\s+body\b/, bodyParts: ['WHOLEBODY'] },
];

const LATERAL_WORDS = 'hips?|knees?|shoulders?|elbows?|wrists?|hands?|ankles?|foot|feet|femur|tibia|humerus|forearm|legs?|arms?';
const LEFT_PATTERN = new RegExp(
    `\\b(left|lt|lft|sin|sinister)\\b|\\bl[\\s_-]*(${LATERAL_WORDS})\\b|\\b(${LATERAL_WORDS})[\\s_-]*l\\b`
);
const RIGHT_PATTERN = new RegExp(
    `\\b(right|rt|rgt|dex|dexter)\\b|\\br[\\s_-]*(${LATERAL_WORDS})\\b|\\b(${LATERAL_WORDS})[\\s_-]*r\\b`
);
const BILATERAL_PATTERN = /\bbilat(eral)?\b|\bboth\b|\bbl\b|\bb\/l\b/;

function normalizeText(value) {
    return String(value || '')
        .toLowerCase()
        .replace(/[^a-z0-9/\u2013-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function normalizeBodyPart(value) {
    return String(value || '').toUpperCase().replace(/[^A-Z]/g, '');
}

function titleCase(value) {
    return String(value || '')
        .toLowerCase()
        .split(/[\s_]+/)
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
}

function detectLaterality({ laterality, text }) {
    const code = String(laterality || '').trim().toUpperCase();
    if (code === 'L') return 'left';
    if (code === 'R') return 'right';
    if (code === 'B' || code === 'U') return 'both';

    if (BILATERAL_PATTERN.test(text)) return 'both';
    const hasLeft = LEFT_PATTERN.test(text);
    const hasRight = RIGHT_PATTERN.test(text);
    if (hasLeft && hasRight) return 'both';
    if (hasLeft) return 'left';
    if (hasRight) return 'right';
    return null;
}

function expandLateralRegion(regionName, laterality) {
    if (laterality === 'left') return [`Left ${regionName}`];
    if (laterality === 'right') return [`Right ${regionName}`];
    if (laterality === 'both') return [`Left ${regionName}`, `Right ${regionName}`];
    return [regionName];
}

export function modalityLabel(code) {
    const normalized = String(code || '').trim().toUpperCase();
    if (!normalized || NON_IMAGE_MODALITIES.has(normalized)) return null;
    return MODALITY_LABELS[normalized] || normalized;
}

export function deriveAnatomyTags({ bodyPartExamined, laterality, descriptions = [] }) {
    const bodyPart = normalizeBodyPart(bodyPartExamined);
    const text = normalizeText(descriptions.filter(Boolean).join(' | '));
    const found = new Set();

    const side = detectLaterality({ laterality, text });

    LATERAL_REGIONS.forEach((region) => {
        if (region.bodyParts.includes(bodyPart) || region.pattern.test(text)) {
            expandLateralRegion(region.name, side).forEach((t) => found.add(t));
        }
    });

    CENTRAL_REGIONS.forEach((region) => {
        if (region.bodyParts.includes(bodyPart) || region.pattern.test(text)) {
            found.add(region.name);
        }
    });

    if (found.has('Lumbar Spine') || found.has('Cervical Spine') || found.has('Thoracic Spine') || found.has('Sacrum')) {
        found.delete('Spine');
    }

    if (!found.size && bodyPart) {
        found.add(titleCase(bodyPart));
    }

    return Array.from(found);
}

function collapseGenericLateralTags(values) {
    const set = new Set(values);
    LATERAL_REGIONS.forEach(({ name }) => {
        if (set.has(name) && (set.has(`Left ${name}`) || set.has(`Right ${name}`))) {
            set.delete(name);
        }
    });
    return Array.from(set);
}

export function normalizeTag(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
}

export function tagKey(tag) {
    return normalizeTag(tag).toLowerCase();
}

export function dedupeTags(tags) {
    const seen = new Set();
    const out = [];
    (tags || []).forEach((raw) => {
        const value = normalizeTag(raw);
        if (!value) return;
        const key = tagKey(value);
        if (seen.has(key)) return;
        seen.add(key);
        out.push(value);
    });
    return out;
}

export function sortTags(tags) {
    return [...(tags || [])].sort((a, b) => a.localeCompare(b));
}

export function studyYearTag(studyDate) {
    const match = String(studyDate || '').match(/\d{4}/);
    return match ? match[0] : null;
}

export function deriveStudyTags(study) {
    const modalities = new Set();
    const anatomy = new Set();
    const year = studyYearTag(study?.studyDate);

    (study?.series || []).forEach((series) => {
        const modality = modalityLabel(series.modality);
        if (modality) modalities.add(modality);

        deriveAnatomyTags({
            bodyPartExamined: series.bodyPartExamined,
            laterality: series.laterality,
            descriptions: [series.seriesDescription, series.protocolName],
        }).forEach((v) => anatomy.add(v));
    });

    const studyLevel = deriveAnatomyTags({ descriptions: [study?.description] });
    if (!anatomy.size) {
        studyLevel.forEach((v) => anatomy.add(v));
    } else {
        const hasLateralAnatomy = Array.from(anatomy).some((v) => /^(Left|Right) /.test(v));
        studyLevel.forEach((v) => {
            if (!/^(Left|Right) /.test(v) || !hasLateralAnatomy) anatomy.add(v);
        });
    }

    return dedupeTags([
        ...Array.from(modalities),
        ...collapseGenericLateralTags(Array.from(anatomy)),
        ...(year ? [year] : []),
    ]);
}

export function addTag(tags, value) {
    return dedupeTags([...(tags || []), value]);
}

export function removeTag(tags, value) {
    const key = tagKey(value);
    return (tags || []).filter((t) => tagKey(t) !== key);
}

export function collectTagOptions(studies) {
    const byKey = new Map();
    (studies || []).forEach((study) => {
        (study.tags || []).forEach((tag) => {
            const key = tagKey(tag);
            if (!key) return;
            const entry = byKey.get(key) || { value: normalizeTag(tag), count: 0 };
            entry.count += 1;
            byKey.set(key, entry);
        });
    });
    return Array.from(byKey.values()).sort((a, b) => a.value.localeCompare(b.value));
}

function dateKey(study) {
    return String(study?.studyDate || '').replace(/\D/g, '');
}

export function sortStudiesByDateDesc(studies) {
    return [...(studies || [])].sort((a, b) => {
        const ka = dateKey(a);
        const kb = dateKey(b);
        if (!ka && !kb) return 0;
        if (!ka) return 1;
        if (!kb) return -1;
        if (ka === kb) return 0;
        return ka < kb ? 1 : -1;
    });
}

function studySearchText(study) {
    const parts = [
        study?.description,
        study?.studyDate,
        ...(study?.tags || []),
        ...(study?.series || []).flatMap((s) => [s.seriesDescription, s.label, s.bodyPartExamined, s.modality]),
    ];
    return parts.filter(Boolean).join(' ').toLowerCase();
}

export function matchesSearch(study, query) {
    const q = String(query || '').trim().toLowerCase();
    if (!q) return true;
    const haystack = studySearchText(study);
    return q.split(/\s+/).every((term) => haystack.includes(term));
}

export function matchesTagFilters(study, selectedTagKeys) {
    const selected = Array.from(selectedTagKeys || []);
    if (!selected.length) return true;
    const studyKeys = new Set((study?.tags || []).map(tagKey));
    return selected.some((key) => studyKeys.has(key));
}

export function filterStudies(studies, { search, selectedTagKeys }) {
    return (studies || []).filter(
        (s) => matchesSearch(s, search) && matchesTagFilters(s, selectedTagKeys)
    );
}
